import React from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    Tooltip,
    Chip,
    Typography
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';

const WeekCard = ({ 
    weekRange, 
    timecards, 
    approvedUserCount,
    onViewTimecard,
    sortConfig,
    onSort 
}) => {
    const getStatusChip = (timecard, isMobile = false) => {
        const { status, approvedAt } = timecard;
        const statusColors = {
            'Uncertified': 'error',
            'Partially Certified': 'warning',
            'Certified': 'primary',
            'Partially Approved': 'warning',
            'Approved': 'success',
            'Timecard Not Created': 'warning'
        };

        // Mobile status labels mapping
        const mobileStatusLabels = {
            'Uncertified': 'U',
            'Certified': 'C',
            'Timecard Not Created': 'NC',
            'Approved': 'A',
            'Partially Approved': 'PA',
            'Partially Certified': 'PC'
        };

        let label = isMobile ? (mobileStatusLabels[status] || status) : status;
        let color = statusColors[status];
        let customStyle = {};

        // Special styling for "Timecard Not Created" status
        if (status === 'Timecard Not Created') {
            customStyle = {
                backgroundColor: '#9c3aad',
                color: '#FFFFFF'
            };
        }

        if (status === 'Approved' && approvedAt && !isMobile) {
            const approvedDate = new Date(approvedAt);
            const now = new Date();
            const hoursAgo = Math.floor((now - approvedDate) / (1000 * 60 * 60));
            label = `Approved ${hoursAgo}h ago`;
        }

        return (
            <Chip
                label={label}
                color={status === 'Timecard Not Created' ? undefined : color}
                size="small"
                sx={{
                    ...(status === 'Timecard Not Created' ? customStyle : {}),
                    ...(isMobile ? {
                        minWidth: 'auto',
                        height: '20px',
                        fontSize: '0.65rem',
                        borderRadius: '8px',
                        '& .MuiChip-label': {
                            padding: '0 4px',
                        }
                    } : {})
                }}
            />
        );
    };

    // Helper function to extract last name from full name
    const getLastName = (fullName) => {
        const nameParts = fullName.trim().split(' ');
        return nameParts[nameParts.length - 1].toLowerCase();
    };

    // Sort timecards within this week
    const sortedTimecards = React.useMemo(() => {
        if (!timecards.length) return [];
        
        return [...timecards].sort((a, b) => {
            let primaryComparison = 0;
            
            if (sortConfig.field === 'status') {
                const statusA = a.status;
                const statusB = b.status;
                
                primaryComparison = sortConfig.direction === 'asc'
                    ? statusA.localeCompare(statusB)
                    : statusB.localeCompare(statusA);
            } else if (sortConfig.field === 'project') {
                // Sort by project name, treating undefined/empty as "Unassigned" for sorting
                const projectA = (a.project?.name || 'Unassigned').toLowerCase();
                const projectB = (b.project?.name || 'Unassigned').toLowerCase();
                
                primaryComparison = sortConfig.direction === 'asc'
                    ? projectA.localeCompare(projectB)
                    : projectB.localeCompare(projectA);
            } else if (sortConfig.field === 'user') {
                // Sort by user last name
                const lastNameA = getLastName(a.user.name);
                const lastNameB = getLastName(b.user.name);
                
                primaryComparison = sortConfig.direction === 'asc'
                    ? lastNameA.localeCompare(lastNameB)
                    : lastNameB.localeCompare(lastNameA);
            }
            
            return primaryComparison;
        });
    }, [timecards, sortConfig]);

    return (
        <Paper sx={{ mb: 8, overflow: 'hidden' }}>
            {/* Card Header */}
            <Box sx={{ 
                p: 2, 
                backgroundColor: '#8f8f8f',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                '@media (max-width:600px)': {
                    p: 1,
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 0.5
                }
            }}>
                <Box sx={{
                    '@media (max-width:600px)': {
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.25
                    }
                }}>
                    <Typography variant="h5" sx={{
                        fontSize: '1.25rem',
                        fontWeight: 'bold',
                        color: 'white',
                        '@media (max-width:600px)': {
                            fontSize: '1.1rem',
                            lineHeight: 1.2
                        }
                    }}>
                        <Box component="span" sx={{
                            '@media (max-width:600px)': {
                                display: 'block'
                            }
                        }}>
                            {(() => {
                                // Extract week number and date range
                                const match = weekRange.match(/^(Week \d+) \((.+)\)$/);
                                if (match) {
                                    return (
                                        <>
                                            <Box component="span" sx={{
                                                '@media (min-width:601px)': {
                                                    display: 'inline'
                                                },
                                                '@media (max-width:600px)': {
                                                    display: 'block'
                                                }
                                            }}>
                                                {match[1]} Timecards
                                            </Box>
                                            <Box component="span" sx={{
                                                '@media (min-width:601px)': {
                                                    display: 'inline',
                                                    marginLeft: 1
                                                },
                                                '@media (max-width:600px)': {
                                                    display: 'block',
                                                    fontSize: '0.9rem',
                                                    fontWeight: 500,
                                                    opacity: 0.9
                                                }
                                            }}>
                                                ({match[2]})
                                            </Box>
                                        </>
                                    );
                                }
                                return `${weekRange} Timecards`;
                            })()}
                        </Box>
                    </Typography>
                </Box>
                <Typography variant="body1" sx={{
                    color: 'white',
                    fontSize: '1.1rem',
                    fontWeight: 500,
                    '@media (max-width:600px)': {
                        fontSize: '0.9rem'
                    }
                }}>
                    Approved Users: {approvedUserCount}
                </Typography>
            </Box>
            
            {/* Table */}
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell 
                            onClick={() => onSort('user')}
                            sx={{ 
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                userSelect: 'none',
                                width: '25%',
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                    fontSize: '0.7rem',
                                    width: '28%',
                                }
                            }}
                        >
                            User
                            {sortConfig.field === 'user' && (
                                <span style={{ marginLeft: '4px' }}>
                                    {sortConfig.direction === 'asc' ? '▲' : '▼'}
                                </span>
                            )}
                        </TableCell>
                        <TableCell 
                            onClick={() => onSort('project')}
                            sx={{ 
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                userSelect: 'none',
                                width: '25%',
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                    fontSize: '0.7rem',
                                    width: '20%',
                                }
                            }}
                        >
                            Project
                            {sortConfig.field === 'project' && (
                                <span style={{ marginLeft: '4px' }}>
                                    {sortConfig.direction === 'asc' ? '▲' : '▼'}
                                </span>
                            )}
                        </TableCell>
                        <TableCell 
                            onClick={() => onSort('status')}
                            align="center"
                            sx={{ 
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                                userSelect: 'none',
                                width: '30%',
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                    fontSize: '0.7rem',
                                    width: '32%',
                                }
                            }}
                        >
                            Status
                            {sortConfig.field === 'status' && (
                                <span style={{ marginLeft: '4px' }}>
                                    {sortConfig.direction === 'asc' ? '▲' : '▼'}
                                </span>
                            )}
                        </TableCell>
                        <TableCell 
                            align="right"
                            sx={{
                                width: '20%',
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                    fontSize: '0.7rem',
                                    width: '20%',
                                }
                            }}
                        >
                            Actions
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedTimecards.map((timecard) => (
                        <TableRow 
                            key={timecard._id}
                            sx={{
                                bgcolor: timecard.status === 'Uncertified' ? 'error.lighter' : 'inherit'
                            }}
                        >
                            <TableCell sx={{
                                width: '25%',
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                    fontSize: '0.7rem',
                                    width: '28%',
                                }
                            }}>
                                {timecard.user.name}
                            </TableCell>
                            <TableCell sx={{
                                width: '25%',
                                wordWrap: 'break-word',
                                whiteSpace: 'normal',
                                '@media (max-width:600px)': {
                                    padding: '4px',
                                    fontSize: '0.7rem',
                                    width: '20%',
                                    lineHeight: 1.2,
                                }
                            }}>
                                {timecard.project?.name || 'Unassigned'}
                            </TableCell>
                            <TableCell 
                                align="center"
                                sx={{
                                    width: '30%',
                                    '@media (max-width:600px)': {
                                        padding: '4px',
                                        fontSize: '0.7rem',
                                        width: '32%',
                                    }
                                }}
                            >
                                <Box sx={{
                                    display: { xs: 'none', md: 'block' }
                                }}>
                                    {getStatusChip(timecard, false)}
                                </Box>
                                <Box sx={{
                                    display: { xs: 'block', md: 'none' }
                                }}>
                                    {getStatusChip(timecard, true)}
                                </Box>
                            </TableCell>
                            <TableCell 
                                align="right"
                                sx={{
                                    width: '20%',
                                    '@media (max-width:600px)': {
                                        padding: '4px',
                                        fontSize: '0.7rem',
                                        width: '20%',
                                    }
                                }}
                            >
                                <Tooltip title={timecard.virtual ? "View Missing Timecard Info" : "View Details"}>
                                    <IconButton
                                        size="small"
                                        onClick={() => onViewTimecard(timecard)}
                                        sx={{
                                            '@media (max-width:900px)': {
                                                padding: '4px',
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: '1rem',
                                                }
                                            }
                                        }}
                                    >
                                        <VisibilityIcon />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Paper>
    );
};

export default WeekCard;
